import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { IntlShape, useIntl } from 'react-intl';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router';
import { AuthContext } from '../../Auth/components/AuthProvider';
import {
  ApiResponse,
  ExternalIdDTO,
  Hierarchy,
  HierarchyApi,
  ProjectApi,
  ProjectDTO,
  ProjectFundingReferenceDTO,
  ProjectFundingReferenceExternalIdentifierDTO,
  ProjectFundingReferenceFundingStreamDTO,
  ProjectFundingReferenceFundingSubstreamDTO,
  ProjectInstitutionalIdDTO,
  ProjectOtherNameDTO,
  ProjectOtherPartnerDTO,
  ProjectToPersonDTO,
  ReferencedOrganisationDTO,
  ReferencedProjectDTO,
  UrlDTO,
} from '../../api/core';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { Loading } from '../system/Loading';
import { Row } from './components/layout/Row';
import { renderDetailAccordionTab } from './components/layout/renderDetailAccordionTab';
import View from './components/layout/View';
import { Button } from 'primereact/button';
import { BreakingList } from './components/layout/BreakingList';
import { ExternalId } from './components/ExternalId';
import { InstitutionalId } from './components/InstitutionalId';
import { getNotation } from '../../Vocabulary/util';
import SearchResultsWork from '../dashboard/SearchResultsWork';
import SearchResultsPerson from '../dashboard/SearchResultsPerson';
import { deleteProject, requestDeleteProject } from '../../util/actions/project-actions';
import { InternalLink } from '../components/InternalLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons/faCaretRight';
import { boolFilter } from '../../Vocabulary/data';
import { SubRow } from './components/layout/Subrow';
import { ExternalLink } from '../components/ExternalLink';
import { localDate, localDateTime } from '../../util/intl.utils';
import { ProjectRelationType } from '../../Settings/data';
import { Stamp } from '../components/Stamp';
import { UrlRender, UrlsRow } from './components/UrlsRow';
import { SubjectsRow } from './components/SubjectsRow';
import { LinkedPerson } from './components/LinkedPerson';
import { LinkedPersonOther } from './components/LinkedPersonOther';

export const DetailProject = (): ReactNode => {
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const { actions, authorizer } = useContext(AuthContext);
  const projectApi: ProjectApi = new ProjectApi().withMiddleware(getAuthMiddleware(actions));
  const apiHierarchy: HierarchyApi = new HierarchyApi().withMiddleware(getAuthMiddleware(actions));

  const [project, setProject] = useState<ProjectDTO>();
  const [hierarchy, setHierarchy] = useState<Hierarchy[]>(null);

  useEffect(() => {
    if (params.id === undefined) {
      return void navigate('/search/projects');
    }
    const id: number = Number(params.id);
    if (isNaN(id)) {
      toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
      return void navigate('/error/404');
    }
    if (!project || id !== project.id) {
      window.scrollTo(0, 0);
      void fetchData(id);
    }
  }, [params.id]);

  const fetchData = async (projectId: number): Promise<void> => {
    try {
      const response: ApiResponse<ProjectDTO> = await projectApi.getProjectReducedRaw({ projectId });
      if (!response.raw.ok) {
        toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
        return navigate(`/error/${response?.raw?.status || 500}`);
      }
      setProject(await response.value());
    } catch (error) {
      toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
      return void navigate(`/error/${error?.status || 500}`);
    }
    const projectHierarchyResponse: ApiResponse<Hierarchy[]> = await apiHierarchy.getProjectHierarchyRaw({ projectId });
    if (projectHierarchyResponse.raw.ok) {
      setHierarchy(await projectHierarchyResponse.value());
    } else {
      setHierarchy([]);
      toast.notify({
        summary: intl.formatMessage({ id: 'error.loading.hierarchy.failed' }),
        severity: 'error',
        sticky: true,
      });
    }
  };

  if (!project ) {
    return <Loading />;
  }

  // RENDER DATA FOR HEADER

  const renderOtherNames = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.otherNames || !projectDTO.otherNames.length) return <></>;
    return (
      <BreakingList>
        {projectDTO.otherNames.map((otherName: ProjectOtherNameDTO): string => otherName.otherName)}
      </BreakingList>
    );
  };

  const renderPeriod = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.startDate && !projectDTO.endDate) return <></>;
    return (
      <div>{`${localDate(projectDTO.startDate, intl.locale)} - ${localDate(projectDTO.endDate, intl.locale)}`}</div>
    );
  };

  const renderExternalIds = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.externalIds || !projectDTO.externalIds.length) return <></>;
    return (
      <BreakingList separator={''}>
        {(projectDTO.externalIds || []).map(
          (externalIdDTO: ExternalIdDTO, index: number): ReactNode => (
            <div key={index}>
              <ExternalId id={externalIdDTO} entity={'project'} />
            </div>
          )
        )}
      </BreakingList>
    );
  };

  const renderInstitutionalIds = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.projectInstitutionalIds || !projectDTO.projectInstitutionalIds.length) return <></>;
    return (
      <BreakingList separator={''}>
        {projectDTO.projectInstitutionalIds.map(
          (institutionalId: ProjectInstitutionalIdDTO, index: number): ReactNode => (
            <div key={index}>
              <InstitutionalId id={institutionalId} entity={'project'} />
            </div>
          )
        )}
      </BreakingList>
    );
  };

  const renderHierarchy = (hid: number, tree: Hierarchy[]): ReactNode => {
    if (!tree || !tree.length) return <></>;
    return (
      <BreakingList separator={''}>
        {tree
          .sort((hierarchy1: Hierarchy, hierarchy2: Hierarchy): number =>
            hierarchy1.stage > hierarchy2.stage ? -1 : 1
          )
          .map(
            (hierarchyItem: Hierarchy, index: number): ReactNode => (
              <div key={index} className={'flex gap-1 align-content-center'}>
                <span className={'inline md:hidden'} style={{ width: `${0.5 * index}rem` }}></span>
                {index !== 0 && (
                  <span className={'text-400 text-xl'}>
                    <FontAwesomeIcon icon={faCaretRight} />
                  </span>
                )}
                {hid === hierarchyItem.entityId ? (
                  <span className={'flex align-items-center'}>{hierarchyItem.name}</span>
                ) : (
                  <InternalLink to={`/project/${hierarchyItem.entityId}`}>{hierarchyItem.name}</InternalLink>
                )}
              </div>
            )
          )}
      </BreakingList>
    );
  };

  const renderHeaderPartners = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.partner || !projectDTO.partner.length) return <></>;
    return (
      <div className={'flex gap-1'}>
        <div>{intl.formatMessage({ id: 'project.attr.partners.label' })}:</div>
        <BreakingList separator={' - '}>
          {(projectDTO.partner || []).map((partner: ReferencedOrganisationDTO): ReactNode => {
            return <InternalLink to={`/organisation/${partner.id}`}>{partner.name}</InternalLink>;
          })}
        </BreakingList>
      </div>
    );
  };

  const renderPredecessors = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.predecessors || !projectDTO.predecessors.length) return <></>;
    return (
      <Row title={getNotation('project_relation_type_list', ProjectRelationType.PREDECESSOR, intl.locale)}>
        {(projectDTO.predecessors || []).map((predecessor: ReferencedProjectDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div>
              <InternalLink to={`/project/${predecessor.id}`}>{predecessor.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${predecessor.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderSuccessors = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.successor || !projectDTO.successor.length) return <></>;
    return (
      <Row title={getNotation('project_relation_type_list', ProjectRelationType.SUCCESSOR, intl.locale)}>
        {(projectDTO.successor || []).map((successor: ReferencedProjectDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div key={index}>
              <InternalLink to={`/project/${successor.id}`}>{successor.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${successor.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderParents = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.parents || !projectDTO.parents.length) return <></>;
    return (
      <Row title={getNotation('project_relation_type_list', ProjectRelationType.PARENT, intl.locale)}>
        {(projectDTO.parents || []).map((parent: ReferencedProjectDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div key={index}>
              <InternalLink to={`/project/${parent.id}`}>{parent.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${parent.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderPartners = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.partner || !projectDTO.partner.length) return <></>;
    return (
      <Row title={intl.formatMessage({ id: 'project.attr.partners.label' })}>
        {(projectDTO.partner || []).map((partner: ReferencedOrganisationDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div key={index}>
              <InternalLink to={`/organisation/${partner.id}`}>{partner.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${partner.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderChilds = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.childs || !projectDTO.childs.length) return <></>;
    return (
      <Row title={getNotation('project_relation_type_list', ProjectRelationType.CHILD, intl.locale)}>
        {(projectDTO.childs || []).map((child: ReferencedProjectDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div key={index}>
              <InternalLink to={`/project/${child.id}`}>{child.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${child.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderOtherPartners = (projectDTO: ProjectDTO): ReactNode => {
    if (!projectDTO.projectOtherPartners || !projectDTO.projectOtherPartners.length) return <></>;
    return (
      <Row title={intl.formatMessage({ id: 'project.attr.otherPartners.label' })}>
        {projectDTO.projectOtherPartners.map((partner: ProjectOtherPartnerDTO): ReactNode => {
          return partner.otherPartner;
        })}
      </Row>
    );
  };

  // RENDER DATA FOR DETAILS

  const renderMembersData = (projectDTO: ProjectDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (projectDTO.projectPersons && projectDTO.projectPersons.length) {
      rows.push(<LinkedPerson linkedPersons={projectDTO.projectPersons} rolesListSubset={'project'} />);
    }
    if (projectDTO.projectPersonOthers && projectDTO.projectPersonOthers.length) {
      rows.push(<LinkedPersonOther linkedPersons={projectDTO.projectPersonOthers} rolesListSubset={'project'} />);
    }
    return renderDetailAccordionTab(intl, rows, 'project.header.members');
  };
  // RENDER DATA FOR DETAILS

  const renderRelationData = (projectDTO: ProjectDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (projectDTO.predecessors && projectDTO.predecessors.length) {
      rows.push(renderPredecessors(projectDTO));
    }
    if (projectDTO.successor && projectDTO.successor.length) {
      rows.push(renderSuccessors(projectDTO));
    }
    if (projectDTO.parents && projectDTO.parents.length) {
      rows.push(renderParents(projectDTO));
    }
    if (projectDTO.childs && projectDTO.childs.length) {
      rows.push(renderChilds(projectDTO));
    }
    if (projectDTO.partner && projectDTO.partner.length) {
      rows.push(renderPartners(projectDTO));
    }
    if (projectDTO.projectOtherPartners && projectDTO.projectOtherPartners.length) {
      rows.push(renderOtherPartners(projectDTO));
    }
    return renderDetailAccordionTab(intl, rows, 'project.header.relations');
  };

  const renderNotesData = (projectPTO: ProjectDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (projectPTO.note)
      rows.push(<Row title={intl.formatMessage({ id: 'project.attr.note.label' })}>{projectPTO.note}</Row>);
    return renderDetailAccordionTab(intl, rows, 'project.header.notes');
  };

  const renderAdditionalInformationData = (projectDTO: ProjectDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (projectDTO.projectFundingReferences && projectDTO.projectFundingReferences.length > 0) {
      rows.push(<Row title={intl.formatMessage({id: 'project.attr.fundingReferences.label'})}>{
        projectDTO.projectFundingReferences.map((funder: ProjectFundingReferenceDTO, index: number): ReactNode => {
          return (
            <div key={index} className={'flex flex-column gap-1'}>
              {funder.funderName && (
                <SubRow>
                  <div>{intl.formatMessage({ id: 'project.attr.fundingReferences.attr.funderName.label' })}</div>
                  <div>{getNotation('funder_list', funder.funderName, intl.locale)}</div>
                </SubRow>
              )}
              {funder.projectFundingReferenceFundingStreams &&
                funder.projectFundingReferenceFundingStreams.filter(
                  (stream: ProjectFundingReferenceFundingStreamDTO): boolean => !!stream.fundingStream
                ).length > 0 && (
                  <SubRow>
                    <div>{intl.formatMessage({ id: 'project.attr.fundingReferences.attr.fundingStreams.label' })}</div>
                    <div className={'flex flex-column gap-1'}>
                      {funder.projectFundingReferenceFundingStreams
                        .filter((stream: ProjectFundingReferenceFundingStreamDTO): boolean => !!stream.fundingStream)
                        .map(
                          (
                            stream: ProjectFundingReferenceFundingStreamDTO,
                            index1: number,
                            array: ProjectFundingReferenceFundingStreamDTO[]
                          ): ReactNode => (
                            <div key={index1}>
                              <span>
                                {getNotation(
                                  'funding_stream_list',
                                  stream.fundingStream,
                                  intl.locale,
                                  funder.funderName
                                )}
                              </span>
                              {index1 + 1 < array.length && <span>, </span>}
                            </div>
                          )
                        )}
                    </div>
                  </SubRow>
                )}
              {funder.projectFundingReferenceFundingSubstreams &&
                funder.projectFundingReferenceFundingSubstreams.filter(
                  (subStream: ProjectFundingReferenceFundingSubstreamDTO): boolean => !!subStream.fundingSubstream
                ).length > 0 && (
                  <SubRow>
                    <div>
                      {intl.formatMessage({ id: 'project.attr.fundingReferences.attr.fundingSubStreams.label' })}
                    </div>
                    <div className={'flex flex-column gap-1'}>
                      {funder.projectFundingReferenceFundingSubstreams
                        .filter(
                          (subStream: ProjectFundingReferenceFundingSubstreamDTO): boolean =>
                            !!subStream.fundingSubstream
                        )
                        .map(
                          (
                            subStream: ProjectFundingReferenceFundingSubstreamDTO,
                            index2: number,
                            array: ProjectFundingReferenceFundingSubstreamDTO[]
                          ): ReactNode => (
                            <div key={index2}>
                              {subStream.fundingSubstream}
                              {index2 + 1 < array.length && <span>, </span>}
                            </div>
                          )
                        )}
                    </div>
                  </SubRow>
                )}
              {funder.projectAcronym && (
                <SubRow>
                  <div>{intl.formatMessage({ id: 'project.attr.fundingReferences.attr.projectAcronym.label' })}</div>
                  <div className={'flex flex-column gap-1'}>{funder.projectAcronym}</div>
                </SubRow>
              )}
              {funder.externalProjectIdentifiers && (
                <SubRow>
                  <div>
                    {intl.formatMessage({ id: 'project.attr.fundingReferences.attr.externalProjectIdentifiers.label' })}
                  </div>
                  <div className={'flex flex-column gap-1'}>
                    {funder.externalProjectIdentifiers.map((externalProjectIdentifier: ProjectFundingReferenceExternalIdentifierDTO) => {
                      return <div>{externalProjectIdentifier.identifier}</div>;
                    })}
                  </div>
                </SubRow>
              )}
              {funder.projectTitle && (
                <SubRow>
                  <div>{intl.formatMessage({ id: 'project.attr.fundingReferences.attr.projectTitle.label' })}</div>
                  <div className={'flex flex-column gap-1'}>{funder.projectTitle}</div>
                </SubRow>
              )}
              {funder.projectUri && (
                <SubRow>
                  <div>{intl.formatMessage({ id: 'project.attr.fundingReferences.attr.projectUri.label' })}</div>
                  <div className={'flex flex-column gap-1'}>
                    <ExternalLink href={funder.projectUri}>{funder.projectUri}</ExternalLink>
                  </div>
                </SubRow>
              )}
            </div>
          );
        })
      }</Row>);
    }
    if (projectDTO.projectSubjects && projectDTO.projectSubjects.length > 0) {
      rows.push(<SubjectsRow subjects={projectDTO.projectSubjects} />);
    }
    if (projectDTO.urls && projectDTO.urls.length > 0) {
      rows.push(
        <UrlsRow
          title={intl.formatMessage({ id: 'project.attr.urls.label' })}
          urls={(projectDTO.urls || []).map(
            (url: UrlDTO): UrlRender => ({ url: url.url, type: getNotation('url_list', url.type, intl.locale) })
          )}
        />
      );
    }
    return renderDetailAccordionTab(intl, rows, 'project.header.additionalInformation');
  };

  const renderAdminData = (projectDTO: ProjectDTO): ReactNode => {
    const rows: ReactNode[] = [];
    rows.push(<Row title={intl.formatMessage({ id: 'project.attr.id.label' })}>{projectDTO.id}</Row>);
    if (projectDTO.adminDataProject.origin) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.origin.label' })}>{projectDTO.adminDataProject.origin}</Row>
      );
    }
    if (projectDTO.adminDataProject.changed) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.changed.label' })}>
          {localDateTime(projectDTO.adminDataProject.changed, intl.locale)}
        </Row>
      );
    }
    if (projectDTO.adminDataProject.created) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.created.label' })}>
          {localDateTime(projectDTO.adminDataProject.created, intl.locale)}
        </Row>
      );
    }
    if (projectDTO.adminDataProject.deskman) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.deskman.label' })}>
          {projectDTO.adminDataProject.deskman}
        </Row>
      );
    }
    if (projectDTO.adminDataProject.editorialStatus) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.editorialStatus.label' })}>
          {getNotation('editorial_status_list', projectDTO.adminDataProject.editorialStatus, intl.locale)}
        </Row>
      );
    }
    if (projectDTO.adminDataProject.hidden) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'project.attr.hidden.label' })}>
          {boolFilter(projectDTO.adminDataProject.hidden || false)}
        </Row>
      );
    }
    return renderDetailAccordionTab(intl, rows, 'project.header.administration');
  };

  return (
    <View
      title={project.name}
      header={
        <>
          {renderOtherNames(project)}
          {renderPeriod(project)}
          {renderExternalIds(project)}
          {renderInstitutionalIds(project)}
          {renderHierarchy(project.id, hierarchy)}
          {renderHeaderPartners(project)}
          {project.adminDataProject?.hidden && intl.formatMessage({ id: 'project.attr.adminDataProject.attr.hidden' })}
        </>
      }
      toolbar={{
        left: [],
        center: [
          project.adminDataProject.hidden && (
            <Stamp red={true}>{intl.formatMessage({ id: 'project.attr.hidden.label' })}</Stamp>
          ),
        ],
        right: [
          (project.canEdit || authorizer.hasRoleOrHigher('ROLE_EDITOR')) && (
            <Button
              icon={'pi pi-pencil'}
              onClick={(): void | Promise<void> => navigate(`/project/${project.id}/edit`)}
              title={intl.formatMessage({ id: 'button.edit' })}
            />
          ),
          authorizer.hasRole('ROLE_ADMIN') && (
            <Button
              icon={'pi pi-trash'}
              severity={'danger'}
              onClick={(): void => deleteProject(projectApi, toast, intl, navigate, project.id, project.name)}
              title={intl.formatMessage({ id: 'button.delete' })}
            />
          ),
          authorizer.hasRole('ROLE_EDITOR') && (
            <Button
              icon={'pi pi-trash'}
              severity={'warning'}
              onClick={(): void => requestDeleteProject(projectApi, toast, intl, navigate, project.id, project.name)}
              title={intl.formatMessage({ id: 'button.deleteRequest' })}
            />
          ),
        ],
      }}
      details={[
        renderMembersData(project),
        renderRelationData(project),
        renderNotesData(project),
        renderAdditionalInformationData(project),
        renderAdminData(project),
      ]}
      tabs={[
        {
          title: intl.formatMessage({ id: 'entity.works' }),
          content: (
            <SearchResultsWork
              externalParams={{ queryString: `project_id_belongs_to:${project.id}` }}
            ></SearchResultsWork>
          ),
        },
        {
          title: `${intl.formatMessage({ id: 'entity.persons' })}`,
          content: (
            <SearchResultsPerson
              externalParams={{
                queryString: (project.projectPersons || [{ personName: { personId: 0 } } as ProjectToPersonDTO])
                  .map((person: ProjectToPersonDTO): string => `id:${person?.personName?.personId || 0}`)
                  .join(' OR '),
              }}
            ></SearchResultsPerson>
          ),
        } /* TODO BACKEND ISSUE 1503
      {
        title: `${intl.formatMessage({id: 'entity.persons'})} (${intl.formatMessage({id: 'embeddedDash.includingChilds'})})`,
        content: <SearchResultsPerson externalParams={{queryString: `project_id_recursive:${project.id}`}}></SearchResultsPerson>,
      },*/,
        ,
      ]}
    />
  );
};;

export default DetailProject;
